import React from "react";
import { Link } from "react-router-dom";
import { Plus, Award, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useCampaign } from "@/context/CampaignContext";
import { toast } from "sonner";

export const ProductCard = ({ product }) => {
  const { lang, t } = useLanguage();
  const { add } = useCart();
  const { campaign, inCampaign } = useCampaign();
  const name = lang === "ar" ? product.name_ar : product.name_en;
  const soldOut = (product.stock ?? 1) <= 0;
  const doubled = inCampaign(product.id);

  const onAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    add(product, 1);
    toast.success(t("addedToCart"), { description: name });
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group bg-[#121214] border border-[#27272A] rounded-2xl overflow-hidden hover-lift block"
    >
      <div className="aspect-square overflow-hidden bg-[#0A0A0B] relative">
        <img
          src={product.image_url}
          alt={name}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${soldOut ? "opacity-40 grayscale" : ""}`}
        />
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid={`card-soldout-${product.id}`}>
            <span className="px-3 py-1.5 rounded-full bg-black/70 border border-red-500/50 text-red-300 text-xs font-medium">
              {t("soldOut")}
            </span>
          </div>
        )}
        {!soldOut && product.points > 0 && (
          <div
            data-testid={`card-points-${product.id}`}
            className="absolute top-3 ltr:right-3 rtl:left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0A0A0B]/80 backdrop-blur-sm border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] num-ltr"
          >
            <Award size={11} /> +{product.points}
          </div>
        )}
        {!soldOut && doubled && (
          <div
            data-testid={`card-campaign-${product.id}`}
            className="absolute top-3 ltr:left-3 rtl:right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D4AF37] text-[#0A0A0B] text-[10px] font-bold num-ltr shadow-lg"
          >
            <Sparkles size={11} /> ×{campaign.multiplier}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mb-1">
          {t(product.category)}
        </div>
        <h3 className="font-display text-lg leading-snug mb-3 line-clamp-2 min-h-[2.5rem]">
          {name}
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-[#9CA3AF]">{t("price")}</div>
            <div className="font-display text-xl font-bold text-[#D4AF37] num-ltr">
              {product.price.toFixed(2)} <span className="text-xs text-[#9CA3AF] font-normal">{t("currency")}</span>
            </div>
          </div>
          <button
            onClick={onAdd}
            disabled={soldOut}
            data-testid={`add-to-cart-${product.id}`}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${soldOut ? "bg-[#27272A] text-[#6B7280] cursor-not-allowed" : "btn-gold"}`}
            aria-label={t("addToCart")}
          >
            <Plus size={18} />
          </button>
        </div>
      </div>
    </Link>
  );
};
